import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import RouteErrorFallback from '../components/shared/RouteErrorFallback';
import AuthPage from './AuthPage';
import AgeGatePage from './AgeGatePage';
import ParentConsentPage from './ParentConsentPage';
import OnboardingPage from './OnboardingPage';
import AccountPage from './AccountPage';
import BridgeScreen from '../components/launchpad/BridgeScreen';
import BusinessHQPage from './BusinessHQPage';
import WebsiteBuilderPage from './WebsiteBuilderPage';
import MarketingStudioPage from './MarketingStudioPage';
import PitchDeckPage from './PitchDeckPage';
import ProductCataloguePage from './ProductCataloguePage';
import BusinessDashboardPage from './BusinessDashboardPage';
import SpotlightPage from './SpotlightPage';
import ParentPortalPage from './ParentPortalPage';
import ConfirmEmailPage from './ConfirmEmailPage';
import NotFoundPage from './NotFoundPage';
import MyPlansPage from './MyPlansPage';
import LandingPage from './LandingPage';
import SafetyPage from './SafetyPage';
import TermsPage from './TermsPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import Dashboard from '../app/pages/Dashboard';
import Challenge from '../app/pages/Challenge';
import Progress from '../app/pages/Progress';
import Summary from '../app/pages/Summary';
import StageReview from '../app/pages/StageReview';
import Plans from '../app/pages/Plans';
import Settings from '../app/pages/Settings';
import AdminInbox from '../app/pages/AdminInbox';
import { getSiteMode } from '../lib/siteMode';

// This file's `router` export sits alongside small route-only helper
// components below, so Vite Fast Refresh can't cleanly HMR it — a
// dev-only tradeoff, not a runtime issue, for a route config module.
/* eslint-disable react-refresh/only-export-components */

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function RedirectLaunchpadStage() {
  const { n } = useParams<{ n: string }>();
  return <Navigate to={`/home/challenge/${n ?? '2'}`} replace />;
}

const siteMode = getSiteMode();

const kidRoutes =
  siteMode === 'kid'
    ? [
        { path: '/plans', element: <Protected><MyPlansPage /></Protected> },
        { path: '/launchpad/:planId', element: <Navigate to="/home" replace /> },
        { path: '/launchpad/:planId/stage/:n', element: <RedirectLaunchpadStage /> },
        { path: '/bridge/:planId', element: <Protected><BridgeScreen /></Protected> },
        { path: '/hq/:planId', element: <Protected><BusinessHQPage /></Protected> },
        { path: '/hq/:planId/website', element: <Protected><WebsiteBuilderPage /></Protected> },
        { path: '/hq/:planId/marketing', element: <Protected><MarketingStudioPage /></Protected> },
        { path: '/hq/:planId/pitch', element: <Protected><PitchDeckPage /></Protected> },
        { path: '/hq/:planId/catalogue', element: <Protected><ProductCataloguePage /></Protected> },
        { path: '/hq/:planId/dashboard', element: <Protected><BusinessDashboardPage /></Protected> },
      ]
    : [];

const parentRoutes =
  siteMode === 'parent'
    ? [{ path: '/parent', element: <Protected><ParentPortalPage /></Protected> }]
    : [];

export const router = createBrowserRouter([
  {
    path: '/',
    element:
      siteMode === 'parent'
        ? <Navigate to="/parent" replace />
        : <LandingPage />,
    errorElement: <RouteErrorFallback />,
  },

  // Legal pages — publicly accessible
  { path: '/safety', element: <SafetyPage />, errorElement: <RouteErrorFallback /> },
  { path: '/terms', element: <TermsPage />, errorElement: <RouteErrorFallback /> },
  { path: '/privacy', element: <PrivacyPolicyPage />, errorElement: <RouteErrorFallback /> },

  // App (gamified experience)
  { path: '/play', element: <Navigate to="/home" replace />, errorElement: <RouteErrorFallback /> },
  { path: '/home/onboarding', element: <Navigate to="/home/challenge/1" replace />, errorElement: <RouteErrorFallback /> },
  { path: '/home', element: <Protected><Dashboard /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/home/plans', element: <Protected><Plans /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/home/challenge/:stageId', element: <Protected><Challenge /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/home/progress', element: <Protected><Progress /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/home/summary', element: <Protected><Summary /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/home/review/:stageId', element: <Protected><StageReview /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/settings', element: <Protected><Settings /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/admin', element: <Protected><AdminInbox /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/account', element: <Protected><AccountPage /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/spotlight', element: <Protected><SpotlightPage /></Protected>, errorElement: <RouteErrorFallback /> },
  { path: '/onboarding', element: <Protected><OnboardingPage /></Protected>, errorElement: <RouteErrorFallback /> },
  {
    element: <AppShell />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/auth', element: <AuthPage /> },
      { path: '/age-gate', element: <AgeGatePage /> },
      { path: '/consent/:token', element: <ParentConsentPage /> },
      { path: '/account/confirm-email', element: <ConfirmEmailPage /> },

      ...kidRoutes,
      ...parentRoutes,

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
